import axios, { all } from 'axios';
import { QuestionData, WordListRequestBody, WordJSON, WordData } from './interfaces';
import { apiURI, CATEGORIES, SUBCATEGORIES, QUESTION_TYPES, DEBUG_TYPES, ORIGIN_LANGUAGES, COMMON_ORIGIN_LANGUAGES, NUM_OPTIONS, COMMON_ORIGIN_LINIAGES, ARTICLES, DEBUG, ENVIRONMENT} from './environment';
import { string } from 'curlyquotes';

export {
    getQuestionsArr
};

/**
 *  Default values for minimum date, and maximum date range for date generation
 */
const DEFAULT_DATE_MIN = -1500;
const DEFAULT_DATE_RANGE = 3531;
var extraCount = 0;

const EASY_DATE_QUESTION_MAX_AGE_YEARS = 100;

const EASY_DATE_OPTION_BUCKET_STARTS = [1900, 1950, 2000];

function wordigins_regexp_or(a: string, b: string): string {
    if (!a) return b;
    if (!b) return a;
    return '(?:' + a + ')|(?:' + b + ')';
}

function wordigins_easy_old_date_exclude_regexp(
    maxAgeYears: number = EASY_DATE_QUESTION_MAX_AGE_YEARS,
    nowYear: number = new Date().getFullYear()
): string {
    const cutoffYear = Math.floor(nowYear) - Math.floor(maxAgeYears);
    const neg = '-[0-9]{1,6}';

    if (!isFinite(cutoffYear)) return '';
    if (cutoffYear <= 0) {
        return '^' + neg + '$';
    }

    const s = String(cutoffYear);
    const len = s.length;
    const parts: string[] = [];

    if (len > 1) {
        parts.push('[0-9]{1,' + (len - 1) + '}');
    }

    for (let i = 0; i < len; i++) {
        const prefix = s.slice(0, i);
        const digit = parseInt(s[i], 10);
        const min = (i === 0 && len > 1) ? 1 : 0;
        const max = digit - 1;

        if (max < min) continue;

        let digitPat: string;
        if (min == max) {
            digitPat = String(min);
        } else {
            digitPat = '[' + min + '-' + max + ']';
        }

        const rem = len - i - 1;
        const remPat = (rem > 0) ? ('[0-9]{' + rem + '}') : '';

        parts.push(prefix + digitPat + remPat);
    }

    if (!parts.length) return '^' + neg + '$';

    return '^(?:' + neg + '|(?:' + parts.join('|') + '))$';
}

//for every 5 pixels wide over 360, 1 extra character can fit

const LINE_CHAR_LIMITS = [0, 43, 87, 127, 165, 203];   // <=57 →1 line, <=77 →2, <=99 →3, <=121 →4, <=143 →5, 
const OPTION_SIZE_BY_LINES = [0, 57, 77, 99, 121, 143]; // "size" of a 1/2/3/4/5 line option
const LINE_CHAR_LIMITS_ORDER = [0, 22, 45, 70, 92, 101, 124];   // <=57 →1 line, <=77 →2, <=99 →3, <=121 →4, <=143 →5, 
const OPTION_SIZE_BY_LINES_ORDER = [0, 57, 89, 121, 153, 185, 217]; // "size" of a 1/2/3/4/5 line option
const MAX_PRUNE_ATTEMPTS = 20;
const HEIGHT = Math.max(window.innerHeight || 0, document.documentElement?.clientHeight || 0);
const WIDTH = Math.max(window.innerWidth || 0, document.documentElement?.clientWidth || 0);
const MAX_TOTAL_SIZE_FACTOR = 342 + HEIGHT - 700;

//increase character limits based on width
const extraChars = Math.floor(Math.max(0, WIDTH - 360) / 5);
const maxLen = Math.max(LINE_CHAR_LIMITS.length, LINE_CHAR_LIMITS_ORDER.length);
for (let i = 1; i < maxLen; i++) {
  if (i < LINE_CHAR_LIMITS.length) LINE_CHAR_LIMITS[i] += extraChars;
  if (i < LINE_CHAR_LIMITS_ORDER.length) LINE_CHAR_LIMITS_ORDER[i] += extraChars;
}

function wordigins_estimate_option_lines(text: string | undefined, type: string): number {
    const len = (text ?? '').trim().length;
    if (type === "order") {
        if (len <= LINE_CHAR_LIMITS_ORDER[1]) return 1;
        if (len <= LINE_CHAR_LIMITS_ORDER[2]) return 2;
        if (len <= LINE_CHAR_LIMITS_ORDER[3]) return 3;
        if (len <= LINE_CHAR_LIMITS_ORDER[4]) return 4;
        if (len <= LINE_CHAR_LIMITS_ORDER[5]) return 5;
        if (len <= LINE_CHAR_LIMITS_ORDER[6]) return 6;
        return 7;
    } else {
        if (len <= LINE_CHAR_LIMITS[1]) return 1;
        if (len <= LINE_CHAR_LIMITS[2]) return 2;
        if (len <= LINE_CHAR_LIMITS[3]) return 3;
        if (len <= LINE_CHAR_LIMITS[4]) return 4;
        if (len <= LINE_CHAR_LIMITS[5]) return 5;
        return 6;
    }
}

function wordigins_is_question_too_long_by_lines(q: QuestionData): boolean {
    const opts = q.options || [];
    let total = 0;
    const lineCounts: Array<number> = [];
    const sizes: Array<number> = [];


    for (const opt of opts) {
        const lines = wordigins_estimate_option_lines(opt, q.type);
        lineCounts.push(lines);

        if (q.type === "order") {
            const sz = OPTION_SIZE_BY_LINES_ORDER[lines];
            sizes.push(sz);
            total += sz;
        } else {
            const sz = OPTION_SIZE_BY_LINES[lines];
            sizes.push(sz);
            total += sz;
        }
    }

    const budget =  MAX_TOTAL_SIZE_FACTOR;

    if (total > budget) {
        console.log('[length-check] too tall');
        console.log(q);
    }

    return total > budget;
}

/**
 * Prune any "too tall" questions, then top-up with extra questions,
 * re-filter, re-prune, until we have exactly n or we hit the safety cap.
 */
async function wordigins_prune_length_loop(
    questionsArr: Array<QuestionData>,
    n: number,
    diff: string,
    uuid: string,
    cat: string
): Promise<Array<QuestionData>> {

    let arr: Array<QuestionData> = questionsArr.filter(q => !wordigins_is_question_too_long_by_lines(q));

    let attempts = 0;
    while (arr.length < n && attempts < MAX_PRUNE_ATTEMPTS) {
        await wordigins_extra_questions(arr, n, diff, uuid, cat);
        wordigins_shuffleArr(arr);
        arr = await wordigins_question_filter(arr, diff);

        arr = arr.filter(q => !wordigins_is_question_too_long_by_lines(q));

        attempts++;
        if (DEBUG) {
            console.log('[length-prune] attempt', attempts, 'now', arr.length);
        }
    }

    if (arr.length > n) {
        arr = arr.slice(0, n);
    }

    if (DEBUG && arr.length < n) {
        console.warn('[length-prune] Could not reach target n with current thresholds', {
            have: arr.length,
            need: n,
            attempts: attempts
        });
    }

    return arr;
}

/**
 * Helper function to shuffle an array in place
 * @param arr array to process
 */
function wordigins_shuffleArr(arr: Array<any>) {
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
}

/**
 * Ensures that: 
 *  Two of the same question type don't appear in a row
 *  There is a maximum of two category-related functions
 *  Each subject word only appears once
 *  Limits date-range-include, date-range-exclude, date, order, first, and last questions to one per game in easy mode
 * @param arr array to process
 */
async function wordigins_question_filter(arr: Array<any>, diff: string) {
    let newArr: Array<QuestionData> = [];
    let shuffles = 0;
    let cat_qs = 0;
    let first_last_count = 0;
    let easyDateCount = 0;

    if (arr.length > 0) {
        newArr[0] = arr.shift();

        // Increment counters if the first question is of a specific type
        if (newArr[0].type === "category-exclude" || newArr[0].type === "category-include") {
            //cat_qs++;
        }
        if (newArr[0].type === "first" || newArr[0].type === "last" ) {
            //first_last_count++;
        }
        if (["date-range-include", "date-range-exclude", "date", "order", "first", "last"].includes(newArr[0].type) && diff === "easy") {
            //easyDateCount++;
        }
    }

    while (arr.length > 0) {
        let unique_word = true;
        for (let i = 0; i < newArr.length; i++) {
            if (arr[0].subject === newArr[i].subject) {
                unique_word = false;
            }
        }

        // If unable to shuffle, return shorter array
        if (shuffles === 10) {
            break;
        }

        // Add question if all conditions are met
        if (
            (newArr[newArr.length - 1].type !== arr[0].type) &&
            ((cat_qs < 2) || ((arr[0].type !== "category-exclude") && (arr[0].type !== "category-include"))) &&
            ((first_last_count < 1) || ((arr[0].type !== "first") && (arr[0].type !== "last"))) &&
            (unique_word) &&
            ((easyDateCount < 1) || (!["date-range-include", "date-range-exclude", "date", "order", "first", "last"].includes(arr[0].type) || diff !== "easy"))
            
        ) {
            newArr[newArr.length] = arr.shift();
            shuffles = 0;

            // Increment counters if the added question is of a specific type
            if (newArr[newArr.length - 1].type === "category-exclude" || newArr[newArr.length - 1].type === "category-include") {
                cat_qs++;
            }
            if (newArr[newArr.length - 1].type === "first" || newArr[newArr.length - 1].type === "last") {
                first_last_count++;
            }
            if (["date-range-include", "date-range-exclude", "date", "order", "first", "last"].includes(newArr[newArr.length - 1].type) && diff === "easy") {
                easyDateCount++;
            }
        } else {
            wordigins_shuffleArr(arr);
            shuffles++;
        }
    }
    return newArr;
}

/**
 * Helper function to return n random unique values from an array
 * from https://stackoverflow.com/questions/19269545/how-to-get-a-number-of-random-elements-from-an-array
 * @param arr array to process
 * @param n   number of unique random elements to get, default 1
 * 
 * @return {Array} array of length n random elements from arr
 */
function wordigins_chooseRand(arr: Array<any>, n: number = 1): Array<any> {
    var result = new Array(n),
        len = arr.length,
        taken = new Array(len);
    if (n > len)
        // Silently return empty when overdrawn
        return [];
    while (n--) {
        var x = Math.floor(Math.random() * len);
        result[n] = arr[x in taken ? taken[x] : x];
        taken[x] = --len in taken ? taken[len] : len;
    }
    return result;
}

async function wordigins_extra_questions(questionsArr: Array<QuestionData>, n: number, diff: string, uuid: string, cat: string, exclude_types?: Array<string>) {
    // If array doesn't have enough questions (due to bad request, etc.), build more until it does
    let validQuestionTypes = [...QUESTION_TYPES];
    while (questionsArr.length < n) {
        if (extraCount < 70) {
            extraCount = extraCount + 1;
            questionsArr.forEach(question => {
                if (question.type === "category-include" || question.type === "category-exclude" || question.type === "date-range-exclude" || question.type === "date-range-include" || question.type === "first" || question.type === "last" || question.type === "date") {
                    let index = validQuestionTypes.indexOf(question.type);
                    if (index > -1) {
                        validQuestionTypes.splice(index, 1);
                    }
                }
            });
            let type = wordigins_chooseRand(validQuestionTypes)[0];

            // Exclude date range questions from easy difficulty
            if ((type === "date-range-include" || type === "date-range-exclude") && diff === "easy") {
                continue;
            }

            // Exclude order questions from easy difficulty
            if (type === "order" && diff === "easy") {
                continue;
            }

            if ((type !== "category-include" && type !== "category-exclude") || cat !== "brands") {
                // guard to prevent crash of debug mode
                if ((type !== 'order' && type !== 'first' && type !== 'last') || (DEBUG === false)) {
                    try {
                        let extraQuestionSet: Array<QuestionData> = await getQuestion(uuid, type, diff, diff, cat);
                        if (extraQuestionSet[0] !== undefined) {
                            questionsArr.push(extraQuestionSet[0]);
                        }
                    } catch (e) {
                        console.error(e);
                    }
                }
            }
        } else {
            break;
        }
    }
}

/**
 * Builds a set of questions
 * @param n     number of questions to build
 * @param diff  overall difficulty
 * @param cat   category
 * 
 * @return {promise} promise for an array of QuestionJSON according to the params
 */
async function getQuestionsArr(uuid: string, n: number, diff: string, cat: string, debugWords: Array<string>): Promise<Array<QuestionData>> {

    // Default max number of each question type = (number of questions requested) / (total num question types) rounded up
    // Shuffle types so we don't get the same weights every time
    let shuffledTypes = [...QUESTION_TYPES],
        shuffledDebugTypes = [...DEBUG_TYPES];
    wordigins_shuffleArr(shuffledTypes);
    wordigins_shuffleArr(shuffledDebugTypes);

    // overfetch but only top-up to n if needed ----
    const OVERFETCH = n + 5;
    const POOL_TARGET = Math.min(n + 20, Math.max(n, n + OVERFETCH));
    // -------------------------------------------------------

    // Get questions in parallel
    let questionPromises: Array<Promise<Array<QuestionData>>> = [];
    const baseTypes = (debugWords.length > 0 ? shuffledDebugTypes : shuffledTypes);
    for (let i = 0; i < POOL_TARGET; i++) {
        if (questionPromises.length < POOL_TARGET) {

            let type = baseTypes.length ? baseTypes[i % baseTypes.length] : undefined,
                debugWord = undefined;
            if (debugWords.length > 0) {
                debugWord = debugWords.shift();
            }

            if (!type) {
                console.warn('[getQuestionsArr] skipped empty type at index', i);
                continue;
            }

            // skip if easy diff and type is date-range
            if ((type === "date-range-include" || type === "date-range-exclude") && diff === "easy") {
                continue;
            }

            // Exclude order questions from easy difficulty
            if (type === "order" && diff === "easy") {
                continue;
            }

            if ((type !== "category-include" && type !== "category-exclude") || cat !== "brands") {
                // Prevent debug crash??
                if (!DEBUG || (type !== 'order' && type !== 'first' && type !== 'last')) {
                    questionPromises.push(
                        getQuestion(uuid, type, diff, diff, cat, debugWord).catch(err => {
                            console.error('[getQuestionsArr] builder failed for type:', type, err);
                            return [];
                        })
                    );
                }
            }
        }
    }

    let questionsArr: Array<QuestionData> = [];

    // Restart logic if stuck for 20 seconds
    setTimeout(function () {
        if (questionsArr.length !== n) {
            getQuestionsArr(uuid, n, diff, cat, debugWords);
        }
    }, 200000);

    // Resolve when all requests are settled
    await Promise.all(questionPromises).then(async (questionSets) => {
        // Unpack all question sets into array
        questionSets.forEach(questionSet => {
            questionSet.forEach(question => {
                questionsArr.push(question);
            });
        });

        // Shuffle, then apply question filter constraints
        wordigins_shuffleArr(questionsArr);
        questionsArr = await wordigins_question_filter(questionsArr, diff);

        // If below n after filter, ONLY then use the extra question loop to top up to n
        while (questionsArr.length < n) {
            await wordigins_extra_questions(questionsArr, n, diff, uuid, cat);
            wordigins_shuffleArr(questionsArr);
            questionsArr = await wordigins_question_filter(questionsArr, diff);
        }

        // Height pruning & top-up (lines-based)
        console.log("Pruning tall Questions...");
        console.log("array is " + questionsArr.length + " long");
        questionsArr = await wordigins_prune_length_loop(questionsArr, n, diff, uuid, cat);

        console.log("Attempting to build " + n + " questions");
        console.log("questionsArr length (pre-trim): ", questionsArr.length);

        // Always trim to exactly n (we may have > n due to overfetch)
        if (questionsArr.length > n) {
            questionsArr = questionsArr.slice(0, n);
        }

        console.log("questionsArr length (final): ", questionsArr.length);
        console.log("questionsArr: ", questionsArr);

    });
    extraCount = 0;
    console.log(questionsArr);
    return questionsArr;
}

/**
 * Gets a number of questions with all attributes defined
 * @param type question type
 * @param qDiff question difficulty
 * @param wDiff word difficulty
 * @param cat category
 * @param debugWord word to guarantee as subject or correct answer
 * @param n number of questions requested with these attributes
 */
function getQuestion(uuid: string, type: string, qDiff: string, wDiff: string | undefined, cat: string | undefined, debugWord?: string): Promise<Array<QuestionData>> {
    cat = (cat === 'random') ? undefined : cat;

    if (qDiff === 'easy') {
        var numOptions = NUM_OPTIONS - 1;
    } else if (qDiff === 'moderate') {
        var numOptions = NUM_OPTIONS;
    } else {
        var numOptions = NUM_OPTIONS;
    }

    let getQuestionPromise: Promise<Array<QuestionData>>;
    switch (type) {
        case 'date':
            getQuestionPromise = buildDateQuestions(uuid, qDiff, cat ? false : true, wDiff, cat ? cat : "default", debugWord, numOptions);
            break;
        case 'date-range-include':
            getQuestionPromise = buildDateRangeQuestions(uuid, qDiff, cat ? false : true, 'include', wDiff, cat ? cat : "default", debugWord, numOptions);
            break;
        case 'date-range-exclude':
            getQuestionPromise = buildDateRangeQuestions(uuid, qDiff, cat ? false : true, 'exclude', wDiff, cat ? cat : "default", debugWord, numOptions);
            break;
        case 'order':
            getQuestionPromise = buildOrderQuestions(uuid, qDiff, cat ? false : true, wDiff, cat ? cat : "default", debugWord, numOptions);
            break;
        case 'first':
            getQuestionPromise = buildFirstLastQuestions(uuid, qDiff, cat ? false : true, 'first', wDiff, cat ? cat : "default", numOptions);
            break;
        case 'last':
            getQuestionPromise = buildFirstLastQuestions(uuid, qDiff, cat ? false : true, 'last', wDiff, cat ? cat : "default", numOptions);
            break;
        case 'origin-include':
            getQuestionPromise = buildOriginQuestions(uuid, qDiff, 'include', wDiff, cat ? cat : "default", debugWord, numOptions);
            break;
        case 'origin-exclude':
            getQuestionPromise = buildOriginQuestions(uuid, qDiff, 'exclude', wDiff, cat ? cat : "default", debugWord, numOptions);
            break;
        case 'origin-multi':
            getQuestionPromise = buildOriginMultiQuestions(uuid, qDiff, cat ? false : true, wDiff, cat ? cat : "default", numOptions);
            break;
        case 'subcat-include':
            getQuestionPromise = buildSubcatQuestions(uuid, qDiff, cat ? false : true, 'include', wDiff, cat ? cat : "default", debugWord, numOptions);
            break;
        case 'subcat-exclude':
            getQuestionPromise = buildSubcatQuestions(uuid, qDiff, cat ? false : true, 'exclude', wDiff, cat ? cat : "default", debugWord, numOptions);
            break;
        case 'subcat-origin':
            getQuestionPromise = buildSubcatOriginQuestions(uuid, qDiff, cat ? false : true, wDiff, cat ? cat : "default", debugWord, numOptions);
            break;
        case 'author-include':
            getQuestionPromise = buildAuthorQuestions(uuid, qDiff, false, 'include', wDiff, cat ? cat : "default", debugWord, numOptions);
            break;
        // case 'author-exclude':
        //     getQuestionPromise = buildAuthorQuestions(uuid, qDiff, false, 'exclude', wDiff, cat ? cat : "default", debugWord, numOptions);
        //     break;
        case 'author-origin':
            getQuestionPromise = buildAuthorOriginQuestions(uuid, qDiff, false, wDiff, cat ? cat : "default", debugWord, numOptions);
            break;
        default:
            return Promise.resolve([]);

    }

    return getQuestionPromise;
}

/**
 *  Returns Date questions
 * 
 *  @param  {string}    questionDifficulty  Determines difficulty of option format
 *  @param  {string}    wordDifficulty      Optional API query for word difficulty filter
 *  @param  {string}    category            Optional API query for word category filter
 *  @param  {string}    debugWord           Word to be guaranteed as subject
 *  @return {promise}   promise             Resolves with question object or array of questions, Rejects with any error       
*/
//no check
async function buildDateQuestions(uuid: string, questionDifficulty: string, random_cat: boolean, wordDifficulty?: string, category?: string, debugWord?: string, numOptions: number = 1): Promise<Array<QuestionData>> {

    // Mod year by number according to difficulty, default easy (range of 50 years)
    let mod: number;
    let rand = Math.floor(Math.random() * DEFAULT_DATE_RANGE) + DEFAULT_DATE_MIN,
    date_regexp = '',
    exclude_date_regexp = '',
    range = '',
    prefix: number;
    switch (questionDifficulty) {
        case 'moderate':
            mod = 25;
            break;
        case 'hard':
            mod = 10;
            break;
        default:
            prefix = Math.floor(rand / 100);
            exclude_date_regexp = wordigins_easy_old_date_exclude_regexp();
            mod = 50;
    }

    const DATE_EXCLUDE_AUTHORS_CHANCE = 0.75;
    const DATE_EXCLUDE_TYPE_CHANCE = 0.75;

    // Body of API call
    let body: WordListRequestBody = {
        debug: DEBUG,
        uuid: uuid,
        number: 1,
        random_cat: random_cat,
    }

    if (exclude_date_regexp) body.exclude_date_regexp = exclude_date_regexp;

    if (Math.random() < DATE_EXCLUDE_AUTHORS_CHANCE) body.has_author= false;
    if (Math.random() < DATE_EXCLUDE_TYPE_CHANCE) body.usesubcat= false;

    // Apply word difficulty filter if present
    if (wordDifficulty !== undefined) body.difficulty = wordDifficulty;

    // Apply word category filter if present
    if (category !== undefined) body.category = category;

    // Apply debug word filter if present
    if (debugWord !== undefined) {
        body.debug_word = debugWord;
    }
    function alignDown(n: number, m: number): number {
        const r = n % m;
        return (r < 0) ? n - r - m : n - r;
    }

    function getWindowBounds(range = DEFAULT_DATE_RANGE, min = DEFAULT_DATE_MIN) {
        const end = min + range - 1;
        const windowMin = Math.min(min, end);
        const windowMax = Math.max(min, end);
        return { windowMin, windowMax };
    }

    function randBucketStart(step: number, range = DEFAULT_DATE_RANGE, min = DEFAULT_DATE_MIN): number {
        const { windowMin, windowMax } = getWindowBounds(range, min);

        let first = alignDown(windowMin, step);
        if (first < windowMin) first += step;

        let last = alignDown(windowMax - (step - 1), step);
        if (last > windowMax) last -= step;

        const count = Math.max(1, Math.floor((last - first) / step) + 1);
        const k = Math.floor(Math.random() * count);
        let start = first + k * step;

        if (start === 0) start += (windowMax >= 1 ? step : -step);

        return start;
    }

    // Format strings according to difficulty
    function formatOptionString(date: string) {
        const base = parseInt(date, 10);
        const needsCE = (n: number) => n >= 10 && n <= 999;

        if (mod === 25) {
            if (base >= 0) {
                const start = base;
                const end = base + 24;
                let label = start + '-' + end;
                if (needsCE(start) && needsCE(end)) label += ' CE';
                return label;
            } else {
                const older = base - (25 - 1);
                return Math.abs(older) + '-' + Math.abs(base) + ' BCE';
            }
        } else if (mod === 50) {
            if (base >= 0) {
                const start = base;
                const end = (start === 2000) ? new Date().getFullYear() : (base + 49);
                let label = start + '-' + end;
                if (needsCE(start) && needsCE(end)) label += ' CE';
                return label;
            } else {
                const older = base - (50 - 1);
                return Math.abs(older) + '-' + Math.abs(base) + ' BCE';
            }
        } else {
            if (base >= 0) {
                let label = base + 's';
                if (needsCE(base)) label += ' CE';
                return label;
            } else {
                return Math.abs(base) + 's BCE';
            }
        }
    }

    // Range and minimum for wrong dates, must not be present in arr
    function getRandomRoundedDate(arr: Array<string>, range = DEFAULT_DATE_RANGE, min = DEFAULT_DATE_MIN) {
        let result = randBucketStart(mod, range, min);

        let safety = 0;
        while (arr.includes(formatOptionString(String(result))) && safety < 200) {
            result = randBucketStart(mod, range, min);
            safety++;
        }
        return result;
    }

    let words: Array<WordJSON> = [];
    try {
        let response = await axios.post(apiURI + '/wordsupdated/list2', body, {});
        words = response.data.data;
    } catch (e) {
        throw (e);
    }

    let questionsArr: Array<QuestionData> = [];
    for (let wordID in words) {
        let options: Array<string> = [];
        let answerDate = parseInt(words[wordID].researched_date, 10);
        if (isNaN(answerDate)) continue;
        answerDate = alignDown(answerDate, mod);
        let answerDateString = formatOptionString(String(answerDate));
        options.push(answerDateString);

        let reviewText = words[wordID].sentence;
        let imageURL = wordigins_chooseRand(words[wordID].images)[0];

        // Generate random date/range and insert if not already present
        while (options.length < numOptions - 1) {
            let rnd: number;
            if (questionDifficulty !== 'moderate' && questionDifficulty !== 'hard') {
                const candidates = EASY_DATE_OPTION_BUCKET_STARTS.filter((s) => s !== answerDate);
                const remaining = candidates.filter((s) => !options.includes(formatOptionString(String(s))));
                if (remaining.length === 0) break;
                rnd = remaining[Math.floor(Math.random() * remaining.length)];
            } else {
                rnd = getRandomRoundedDate(options);
            }
            const option = formatOptionString(String(rnd));
            if (!options.includes(option)) options.push(option);
        }

        // Shuffle
        wordigins_shuffleArr(options);

        let question: QuestionData = {
            type: 'date',
            reviewText: reviewText,
            reviewImgURL: imageURL,
            category: category,
            subject: words[wordID].phrase,
            subjectDiff: words[wordID].difficulty,
            options: options,
            answer: answerDateString,
            audioURL: words[wordID].audio
        };
        question.options = question.options.filter((option) => {
            return option !== undefined;
        });
        if (question.options.length === numOptions - 1) {
            questionsArr.push(question);
            if (debugWord) {
                console.log("Built Question for ", debugWord, " Question type: ", "date");
            }
        } else {
            if (debugWord) {
                console.log("Failed to build Question for ", debugWord);
            }
        }
    }

    return questionsArr;
}

/**
 *  Returns Order questions
 * 
 *  @param  {string}    questionDifficulty  Determines how many options to get
 *  @param  {string}    wordDifficulty      Optional API query for word difficulty filter
 *  @param  {string}    category            Optional API query for word category filter
 *  @param  {string}    debugWord           Word to be guaranteed as highlight word
 *  @return {promise}   promise             Resolves with question object or array of questions, Rejects with any error       
 */
async function buildOrderQuestions(uuid: string, questionDifficulty: string, random_cat: boolean, wordDifficulty?: string, category?: string, debugWord?: string, numOptions: number = 1): Promise<Array<QuestionData>> {

    // Body of API call
    const ORDER_EXCLUDE_AUTHORS_CHANCE = 0.5;
    const ORDER_EXCLUDE_TYPES_CHANCE = 0.5;

    let body: WordListRequestBody = {
        debug: DEBUG,
        uuid: uuid,
        number: numOptions,
        random_cat: random_cat,
        //check_similar: true remove wordigins check similar functionality
    }

    if (Math.random() < ORDER_EXCLUDE_AUTHORS_CHANCE) body.has_author = false;
    if (Math.random() < ORDER_EXCLUDE_TYPES_CHANCE) body.usesubcat = false;


    // Apply word difficulty filter if present
    if (wordDifficulty !== undefined) body.difficulty = wordDifficulty;

    // Apply word category filter if present
    if (category !== undefined) body.category = category;

    // Apply debug word filter if present
    if (debugWord !== undefined) body.debug_word = debugWord;

    let words: Array<WordData> = [];

    try {
        let response = await axios.post(apiURI + '/wordsupdated/list2', body, {});

        let wordData: Array<WordJSON> = response.data.data;
        for (const wordID in wordData) {
            words.push({
                word: wordData[wordID].phrase,
                difficulty: wordData[wordID].difficulty,
                date: wordData[wordID].researched_date,
                sentence: wordData[wordID].sentence,
                images: wordData[wordID].images,
                audio: wordData[wordID].audio
            });
        }
    } catch (e) {
        throw (e);
    }

    let questionsArr: Array<QuestionData> = [];
    for (let i = 0; i <= (words.length - numOptions); i += numOptions) {
        let optionsDates: { [key: string]: string } = {};
        // Get the correct number of options
        let options: Array<WordData> = [];
        let optionStrings: Array<string> = [];
        for (let j = 0; j < numOptions - 1; j++) {
            options.push(words[i + j]);
            optionsDates[words[i + j].word] = words[i + j].date;
            optionStrings.push(words[i + j].word);
        }

        // Create an ordered array from clone of the original
        let optionsOrdered = Array.from(options);
        optionsOrdered.sort((a, b) => parseInt(a.date, 10) - parseInt(b.date, 10));

        //sort options alphabetically
        optionStrings.sort();

        let validQuestion = true;

        for (let i = 0; i < optionsOrdered.length - 1; ++i) {
            if (parseInt(optionsOrdered[i].date, 10) === parseInt(optionsOrdered[i + 1].date, 10)) {
                validQuestion = false;
            };
        }

        // Create an answer key of the options order
        let answerOrder: Array<string> = [];
        optionsOrdered.forEach(option => {
            answerOrder.push(option.word);
        });

        // Choose one option for review text/image, use debug word instead if present
        let reviewWord = optionsOrdered[0];
        if (debugWord !== undefined) {
            let debugIx = optionsOrdered.map(e => { return e.word }).indexOf(debugWord);
            reviewWord = optionsOrdered[debugIx];
        }
        if (reviewWord === undefined) {
            reviewWord = optionsOrdered[0];
        }

        let imageURL = wordigins_chooseRand(reviewWord.images)[0];

        let question: QuestionData = {
            type: 'order',
            reviewText: reviewWord.sentence,
            reviewImgURL: imageURL,
            subject: reviewWord.word,
            subjectDiff: reviewWord.difficulty,
            category: category,
            options: optionStrings,
            answer: answerOrder,
            optionsDates: optionsDates,
            audioURL: reviewWord.audio
        }
        question.options = question.options.filter((option) => {
            return option !== undefined;
        });

        if (validQuestion) {
            if (question.options.length === numOptions - 1) {
                questionsArr.push(question);
                if (debugWord) {
                    console.log("Built Question for ", debugWord, " Question type: ", "order");
                }
            } else {
                if (debugWord) {
                    console.log("Failed to build Question for ", debugWord);
                }
            }
        }
    }

    return questionsArr;
}

/**
  *  Returns First/Last questions
  * 
  *  @param  {string}    firstOrLast     Value 'first' or 'last' can be passed to force a type, default false causes random choice
  *  @param  {string}    wordDifficulty  Optional API query for word difficulty filter
  *  @param  {string}    category        Optional API query for word category filter
  *  @return {promise}   promise         Resolves with array of questions, Rejects with any error       
 */
async function buildFirstLastQuestions(uuid: string, questionDifficulty: string, random_cat: boolean, firstOrLast?: string, wordDifficulty?: string, category?: string, numOptions: number = 1): Promise<Array<QuestionData>> {

    // Set type, random if not passed
    let randType = (Math.floor(Math.random() * 2)) ? 'first' : 'last';
    let type = (firstOrLast !== undefined) ? firstOrLast : randType;

    const FIRSTLAST_EXCLUDE_AUTHORS_CHANCE = 0.75;
    const FIRSTLAST_EXCLUDE_TYPE_CHANCE = 0.75;

    // Body for API call
    let body: WordListRequestBody = {
        debug: DEBUG,
        uuid: uuid,
        number: numOptions - 1,
        random_cat: random_cat,
        //check_similar: true remove wordigins check similar functionality
    };

    if (Math.random() < FIRSTLAST_EXCLUDE_AUTHORS_CHANCE) body.has_author= false;
    if (Math.random() < FIRSTLAST_EXCLUDE_TYPE_CHANCE) body.usesubcat= false;

    // Apply word difficulty filter if present
    if (wordDifficulty !== undefined) body.difficulty = wordDifficulty;

    // Apply word category filter if present
    if (category !== undefined) body.category = category;

    let words: Array<{ word: string, date: string, images: Array<string>, reviewText: string, audio: string, difficulty: string }> = [];

    // Get word list
    try {
        let response = await axios.post(apiURI + '/wordsupdated/list2', body, {});

        let wordData: Array<WordJSON> = response.data.data;
        for (let wordID in wordData) {
            words.push({
                word: wordData[wordID].phrase,
                date: wordData[wordID].researched_date,
                images: wordigins_chooseRand(wordData[wordID].images)[0],
                reviewText: wordData[wordID].sentence,
                audio: wordData[wordID].audio,
                difficulty: wordData[wordID].difficulty
            });
        }
    } catch (e) {
        throw (e);
    }

    let questionsArr: Array<QuestionData> = [];
    for (let i = 0; i < words.length; i += (numOptions - 1)) {
        let options: Array<string> = [];
        let optionsDates: { [key: string]: string } = {};

        // Track relevant values
        let max = 0,
            min: number = Number.MAX_SAFE_INTEGER,
            maxOption: string = '',
            minOption: string = '',
            maxOptionDate: number,
            minOptionDate: number,
            maxImages: Array<string> = [],
            minImages: Array<string> = [],
            maxText: string = '',
            minText: string = '',
            maxAudio: string = '',
            minAudio: string = '',
            minDiff: string = '',
            maxDiff: string = '';
        for (let j = 0; j < (numOptions - 1); j++) {
            options.push(words[i + j].word);
            optionsDates[words[i + j].word] = words[i + j].date;
            if (parseInt(words[i + j].date) < min) {
                min = parseInt(words[i + j].date);
                minOption = words[i + j].word;
                minOptionDate = parseInt(words[i + j].date);
                minImages = words[i + j].images;
                minText = words[i + j].reviewText;
                minAudio = words[i + j].audio;
                minDiff = words[i + j].difficulty;
            }
            if (parseInt(words[i + j].date) > max) {
                max = parseInt(words[i + j].date);
                maxOption = words[i + j].word;
                maxOptionDate = parseInt(words[i + j].date);
                maxImages = words[i + j].images;
                maxText = words[i + j].reviewText;
                maxAudio = words[i + j].audio;
                maxDiff = words[i + j].difficulty;
            }
        }

        let maxImgUrl = (Array.isArray(maxImages)) ? wordigins_chooseRand(maxImages)[0] : maxImages;
        let minImgUrl = (Array.isArray(minImages)) ? wordigins_chooseRand(minImages)[0] : minImages;

        let question: QuestionData = {
            type: type,
            options: options,
            subject: (type === 'first') ? minOption : maxOption,
            answer: (type === 'first') ? minOption : maxOption,
            optionsDates: optionsDates,
            subjectDiff: (type === 'first') ? minDiff : maxDiff,
            reviewText: (type === 'first') ? minText : maxText,
            reviewImgURL: (type === 'first') ? minImgUrl : maxImgUrl,
            audioURL: (type === 'first') ? minAudio : maxAudio,
        }
        question.options = question.options.filter((option) => {
            return option !== undefined;
        });

        let validQuestion = true;

        if (type === 'first') {
            words.forEach(word => {
                if (word.word !== minOption) {
                    if (parseInt(word.date) === minOptionDate) {
                        validQuestion = false;
                    }
                }
            });
        } else if (type === 'last') {
            words.forEach(word => {
                if (word.word !== maxOption) {
                    if (parseInt(word.date) === maxOptionDate) {
                        validQuestion = false;
                    }
                }
            });
        }

        //sort options alphabetically without articles
        question.options.sort(function (a, b) {
            if (a !== undefined && b !== undefined) {
                for (var i = 0; i < ARTICLES.length; i++) {
                    a = a.replace(ARTICLES[i] + " ", "");
                    b = b.replace(ARTICLES[i] + " ", "");
                }

                return (a > b) ? 1 : -1;
            }

            return 0;
        });

        if (validQuestion) {
            if (question.options.length === (numOptions - 1)) questionsArr.push(question);
        }
    }

    return questionsArr;
}

/**
 *  Returns Date-Range questions
 * 
 *  @param  {string}    questionDifficulty  Determines difficulty of option format
 *  @param  {string}    includeOrExclude    Value 'include' or 'exclude' can be passed to force a type, default false causes random choice
 *  @param  {string}    wordDifficulty      Optional API query for word difficulty filter
 *  @param  {string}    category            Optional API query for word category filter
 *  @param  {string}    debugWord           Word to be guaranteed as subject
 *  @return {promise}   promise             Resolves with question object or array of questions, Rejects with any error       
*/
//on incorrect for both, need to send word
async function buildDateRangeQuestions(uuid: string, questionDifficulty: string, random_cat: boolean, includeOrExclude?: string, wordDifficulty?: string, category?: string, debugWord?: string, numOptions: number = 1): Promise<Array<QuestionData>> {
    // Set type, random if not passed
    let randType = (Math.floor(Math.random() * 2)) ? 'include' : 'exclude';
    let type = (includeOrExclude) ? includeOrExclude : randType;

    // Create RegExp to filter by random century, decade, half-century. Default century.
    let rand = Math.floor(Math.random() * DEFAULT_DATE_RANGE) + DEFAULT_DATE_MIN,
        date_regexp = '',
        exclude_date_regexp = '',
        range = '',
        prefix: number;

    const buildYearRegex = (startYear: number, endYear: number): string => {
        const lo = Math.min(startYear, endYear);
        const hi = Math.max(startYear, endYear);
        const years: string[] = [];
        for (let y = lo; y <= hi; y++) years.push(String(y));
        return '^(?:' + years.join('|') + ')$';
    };

    const formatRangeLabel = (startYear: number, endYear: number, mode: 'moderate' | 'hard'): string => {
        if (startYear >= 0 && endYear >= 0) {
            if (mode === 'moderate') {
                return String(startYear) + '-' + String(endYear + 1 - 1 + (50 - 49));
            } else {
                return String(startYear) + '-' + String(endYear);
            }
        }
        const left = Math.max(Math.abs(startYear), Math.abs(endYear));
        const right = Math.min(Math.abs(startYear), Math.abs(endYear));
        return left + '-' + right + ' BCE';
    };

    switch (questionDifficulty) {
        case 'moderate': {
            // Half-century window
            prefix = Math.floor(rand / 50);
            { // clamp positive-era half-century so start+49 <= window end
                const windowEnd = DEFAULT_DATE_MIN + DEFAULT_DATE_RANGE - 1;
                if (prefix >= 0) {
                    const maxPrefix50 = Math.floor((windowEnd - 49) / 50);
                    if (prefix > maxPrefix50) prefix = maxPrefix50;
                }
            }
            const startYear = prefix * 50;
            const endYear = startYear + 49 * (startYear >= 0 ? 1 : 1);

            date_regexp = buildYearRegex(startYear, endYear);

            if (startYear >= 0) {
                range = String(startYear) + '-' + String((prefix + 1) * 50);
            } else {
                range = formatRangeLabel(startYear, endYear, 'moderate');
            }

            exclude_date_regexp = date_regexp;
            break;
        }
        case 'hard': {
            prefix = Math.floor(rand / 10);
            { // clamp positive-era decade so start+9 <= window end
                const windowEnd = DEFAULT_DATE_MIN + DEFAULT_DATE_RANGE - 1;
                if (prefix >= 0) {
                    const maxPrefix10 = Math.floor((windowEnd - 9) / 10);
                    if (prefix > maxPrefix10) prefix = maxPrefix10;
                }
            }
            const startYear = prefix * 10;
            const endYear = (startYear >= 0) ? (startYear + 9) : (startYear - 9);

            date_regexp = buildYearRegex(startYear, endYear);

            if (startYear >= 0) {
                range = String(prefix) + '0-' + String(prefix) + '9';
            } else {
                range = formatRangeLabel(startYear, endYear, 'hard');
            }

            exclude_date_regexp = date_regexp;
            break;
        }
        default: { // this will be easy
            prefix = Math.floor(rand / 100);
            date_regexp = '^' + prefix;
            range = (prefix + 1) + 'th century'
            if (category === undefined || category === "imported") {
                exclude_date_regexp = '^1[' + (prefix - 11) + "-" + (prefix - 9) + ']';
            } else {
                exclude_date_regexp = '^' + (prefix - 1) + '5[0-9]|' + (prefix - 1) + "[6-9][0-9]|" + (prefix) + '[0-9][0-9]|' + (prefix + 1) + '[0-4][0-9]';
            }
        }
    }

    let correctBody: WordListRequestBody, incorrectBody: WordListRequestBody;
    if (type === 'include') {
        // API call bodies for odd one out, in range
        correctBody = {
            debug: DEBUG,
            uuid: uuid,
            number: 1,
            date_regexp: date_regexp,
            random_cat: random_cat,
        }
        incorrectBody = {
            debug: false,
            uuid: uuid,
            number: numOptions - 1,
            exclude_date_regexp: exclude_date_regexp,
            random_cat: random_cat,
            english_only: true
            //check_similar: true remove wordigins check similar functionality
        }
    } else if (type === 'exclude') {
        // API call bodies for odd one out, not in range
        incorrectBody = {
            debug: false,
            uuid: uuid,
            number: numOptions - 1,
            date_regexp: date_regexp,
            random_cat: random_cat,
            english_only: true
            //check_similar: true remove wordigins check similar functionality
        }
        correctBody = {
            debug: DEBUG,
            uuid: uuid,
            number: 1,
            exclude_date_regexp: exclude_date_regexp,
            random_cat: random_cat,
        }
    } else {
        return Promise.reject(new Error('Invalid question type: ' + type));
    }

    if (questionDifficulty !== 'moderate' && questionDifficulty !== 'hard') {
        const easy_old_exclude_date_regexp = wordigins_easy_old_date_exclude_regexp();
        if (easy_old_exclude_date_regexp) {
            correctBody.exclude_date_regexp = wordigins_regexp_or((correctBody.exclude_date_regexp || ''), easy_old_exclude_date_regexp);
            incorrectBody.exclude_date_regexp = wordigins_regexp_or((incorrectBody.exclude_date_regexp || ''), easy_old_exclude_date_regexp);
        }
    }

    console.log( " date correct body", correctBody);

    // Apply difficulty filter to all API calls if present
    if (wordDifficulty) {
        correctBody.difficulty = wordDifficulty;
        incorrectBody.difficulty = wordDifficulty;
    }

    // Apply category filter to all API calls if present
    if (category) {
        correctBody.category = category;
        incorrectBody.category = category;
    }

    // Apply debug word filter if present
    if (debugWord !== undefined) correctBody.debug_word = debugWord;

    let correctOptionsArr: Array<string> = [],
        imageURLArr: Array<string> = [],
        reviewTextArr: Array<string> = [],
        audioArr: Array<string> = [],
        subjectDiffArr: Array<string> = [],
        optionsDates: { [key: string]: string } = {};

    try {
        var correct_response = await axios.post(apiURI + '/wordsupdated/list2', correctBody, {});

        let correct_wordData: Array<WordJSON> = correct_response.data.data;
        var correct = "";
        for (let wordID in correct_wordData) {
            correctOptionsArr.push(correct_wordData[wordID].phrase);
            imageURLArr.push(wordigins_chooseRand(correct_wordData[wordID].images)[0]);
            reviewTextArr.push(correct_wordData[wordID].sentence);
            audioArr.push(correct_wordData[wordID].audio);
            subjectDiffArr.push(correct_wordData[wordID].difficulty);
            correct = correct_wordData[wordID].phrase;
            optionsDates[correct_wordData[wordID].phrase] = correct_wordData[wordID].researched_date;
        }
        incorrectBody.correct = correct;
    } catch (e) {
        throw (e);
    }

    let incorrectOptionsArr: Array<string> = [];

    try {
        var incorrect_response = await axios.post(apiURI + '/wordsupdated/list2', incorrectBody, {});

        let incorrect_wordData: Array<WordJSON> = incorrect_response.data.data;
        for (let wordID in incorrect_wordData) {
            incorrectOptionsArr.push(incorrect_wordData[wordID].phrase);
            optionsDates[incorrect_wordData[wordID].phrase] = incorrect_wordData[wordID].researched_date;
        }
    } catch (e) {
        throw (e);
    }

    let questionsArr: Array<QuestionData> = [];
    try {
        await Promise.all([correct_response, incorrect_response]).then(function () {
            correctOptionsArr.forEach(correct => {
                let options: Array<string | undefined> = [];
                options.push(correct);
                while (options.length < numOptions - 1) {
                    options.push(incorrectOptionsArr.pop());
                }

                // sort options alphabetically
                options.sort(function (a, b) {
                    if (a !== undefined && b !== undefined) {
                        for (var i = 0; i < ARTICLES.length; i++) {
                            a = a.replace(ARTICLES[i] + " ", "");
                            b = b.replace(ARTICLES[i] + " ", "");
                        }

                        return (a > b) ? 1 : -1;
                    }

                    return 0;
                });

                let question: QuestionData = {
                    type: 'date-range-' + type,
                    options: options,
                    subject: correct,
                    subjectDiff: subjectDiffArr.shift()!,
                    answer: correct,
                    optionsDates: optionsDates,
                    range: range,
                    reviewImgURL: imageURLArr.shift(),
                    reviewText: reviewTextArr.shift(),
                    audioURL: audioArr.shift(),
                };

                question.options = question.options.filter((option) => {
                    return option !== undefined;
                });

                // If the request couldn't find words that match the requirements, quietly fail - just don't push the question
                if (question.options.length === numOptions - 1) {
                    questionsArr.push(question);
                    if (debugWord) {
                        console.log("Built Question for ", debugWord, " Question type: ", "date-range");
                    }
                } else {
                    if (debugWord) {
                        console.log("Failed to build Question for ", debugWord);
                    }
                }
            });
        });
    } catch (e) {
        throw (e);
    }

    return questionsArr;
}

/**
 *  Returns origin questions e.g. "Which word came from/did not come from French"
 * 
 *  @param  {string}    questionDifficulty  Determines difficulty of option format
 *  @param  {string}    includeOrExclude    Value 'include' or 'exclude' can be passed to force a type, default false causes random choice
 *  @param  {string}    wordDifficulty      Optional API query for word difficulty filter
 *  @param  {string}    category            Optional API query for word category filter
 *  @param  {string}    debugWord           Word to be guaranteed as correct answer
 *  @return {promise}   promise             Resolves with question object or array of questions, Rejects with any error       
*/
//on incorrect for both, need to send word
async function buildOriginQuestions(uuid: string, questionDifficulty: string, includeOrExclude?: string, wordDifficulty?: string, category?: string, debugWord?: string, numOptions: number = 1): Promise<Array<QuestionData>> {
    // Set type, random if not passed
    let randType = (Math.floor(Math.random() * 2)) ? 'include' : 'exclude';
    let type = (includeOrExclude) ? includeOrExclude : randType;

    const ORIGIN_CORRECT_EXCLUDE_AUTHORS_CHANCE = 0.75;
    const ORIGIN_CORRECT_EXCLUDE_TYPE_CHANCE = 0.75;

    // API call bodies for odd one out, with subject language as origin
    let correctBody: WordListRequestBody = {
        debug:  DEBUG,
        uuid:   uuid,
        number: 1,
        min_origin_count: 1,
    }

    if (Math.random() < ORIGIN_CORRECT_EXCLUDE_AUTHORS_CHANCE && type === 'include') correctBody.has_author= false;
    if (Math.random() < ORIGIN_CORRECT_EXCLUDE_TYPE_CHANCE && type === 'include') correctBody.usesubcat= false;

    console.log("origin-include correctBody", correctBody);
    // Apply word difficulty filter if present
    // if (wordDifficulty !== undefined) correctBody.difficulty = wordDifficulty;

    // Apply word category filter if present
    if (category !== undefined) correctBody.category = category;

    // Apply debug word filter if present
    if (debugWord !== undefined) correctBody.debug_word = debugWord;

    let correctOptionsArr: Array<string> = [],
        imageURLArr: Array<string> = [],
        reviewTextArr: Array<string> = [],
        audioArr: Array<string> = [],
        possibleLangs: Array<Array<string>> = [],
        excludelangs: Array<Array<string>> = [],
        subjectDiffArr: Array<string> = [];

        if (wordDifficulty !== undefined) correctBody.origin_difficulty = "easy";

    try {
        var correct_response = await axios.post(apiURI + '/wordsupdated/list2', correctBody, {});

        let correct_wordData: Array<WordJSON> = correct_response.data.data;
        var correct = '';
        for (let wordID in correct_wordData) {
            correctOptionsArr.push(correct_wordData[wordID].phrase);
            imageURLArr.push(wordigins_chooseRand(correct_wordData[wordID].images)[0]);
            reviewTextArr.push(correct_wordData[wordID].sentence);
            audioArr.push(correct_wordData[wordID].audio);
            subjectDiffArr.push(correct_wordData[wordID].difficulty);

            // Push array of possible subject langs language_of_origin that don't appear in origin_exclude
            possibleLangs.push(correct_wordData[wordID].language_of_origin);
            excludelangs.push(correct_wordData[wordID].origin_exclude);
            correct = correct_wordData[wordID].phrase;

            //console.log("length: ", correct_wordData);
            //console.log(correct_wordData[wordID].phrase, " ", "possible: ", possibleLangs[0]);
            //console.log(correct_wordData[wordID].phrase, " ", "exclude: ", excludelangs[0]);
        }
    } catch (e) {
        throw (e);
    }
    let allPossibleLangs = possibleLangs.flat();
    let allExcludeLangs = possibleLangs[0].concat(excludelangs[0]);
    

    // Initial definition of num subject langs limited by difficulty
    let maxNumSubjectLangs = (questionDifficulty === 'easy') ? 1 : (questionDifficulty === 'moderate') ? 2 : undefined;

    let subjectLangs: Array<string> = [];

    let incorrectBody: WordListRequestBody;
    if (type === 'include') {
        // refined max based on number of languages returned 

        // const ORIGIN_INCORRECT_EXCLUDE_AUTHORS_CHANCE = 0.75;
        // const ORIGIN_INCORRECT_EXCLUDE_TYPE_CHANCE = 0.75;

        incorrectBody = {
            debug:  false,
            uuid:   uuid,
            number: numOptions - 1,
            exclude_lang: JSON.stringify(allPossibleLangs),
            origin_exclude: JSON.stringify(allPossibleLangs),
            min_origin_count: 1,
            //check_similar: true, remove wordigins check similar functionality
            correct: correct
        }

        // if (Math.random() < ORIGIN_INCORRECT_EXCLUDE_AUTHORS_CHANCE) incorrectBody.has_author= false;
        // if (Math.random() < ORIGIN_INCORRECT_EXCLUDE_TYPE_CHANCE) incorrectBody.usesubcat= false;

        console.log("origin-include incorrectBody", incorrectBody);

        //if (wordDifficulty !== undefined) incorrectBody.origin_difficulty = "easy";
    } else if (type === 'exclude') {
        // Get all common languages that are not origin languages of the correct answer
        let filteredLangs = COMMON_ORIGIN_LANGUAGES.filter(el => allExcludeLangs.indexOf(el) < 0);

        // refine max based on number of languages found
        let numSubjectLangs = (maxNumSubjectLangs && maxNumSubjectLangs <= allExcludeLangs.length) ?
            maxNumSubjectLangs :
            (allExcludeLangs.length <= filteredLangs.length) ?
                allExcludeLangs.length :
                filteredLangs.length;

        // maybe choose at random from within that range, 0 excluded
        // numSubjectLangs = Math.floor(Math.random() * (numSubjectLangs - 1)) + 1;

        // Get common linages and remove all languages associated with the correct answer
        let filteredLiniages: Array<Array<string>> = [];
        COMMON_ORIGIN_LINIAGES.forEach(lineage => {
            filteredLiniages.push(lineage.filter(el => allExcludeLangs.indexOf(el) < 0));
        });
        // Use only lineages that contain at least the number of subject langs we want
        filteredLiniages = filteredLiniages.filter(el => el.length >= numSubjectLangs);

        // Get random set of subject languages from those available, limited by the above logic
        if (filteredLiniages.length > 0) {
            // Try to limit to a common lineage
            subjectLangs = wordigins_chooseRand(wordigins_chooseRand(filteredLiniages)[0], numSubjectLangs);
        } else {
            // Fallback on random common languages if this is not possible
            subjectLangs = wordigins_chooseRand(filteredLangs, numSubjectLangs);
        }

        // API call bodies for odd one out, without subject language as origin
        incorrectBody = {
            debug:  false,
            uuid:   uuid,
            number: numOptions - 1,
            origin_lang: JSON.stringify(subjectLangs),
            min_origin_count: subjectLangs.length,
            // check_similar: true, remove wordigins check similar functionality
            correct: correct
        }

    } else {
        return Promise.reject(new Error('Invalid question type: ' + type));
    }
    
    // Apply word difficulty filter if present
     if (wordDifficulty !== undefined) incorrectBody.origin_difficulty = "easy";

    // Apply word category filter if present
    if (category !== undefined) incorrectBody.category = category;

    let incorrectOptionsArr: Array<string> = [];
    try {
        var incorrect_response = await axios.post( apiURI +'/wordsupdated/list2', incorrectBody, {});
        let incorrect_wordData: Array<WordJSON> = incorrect_response.data.data;
        for (let wordID in incorrect_wordData) {
            incorrectOptionsArr.push(incorrect_wordData[wordID].phrase);
        }
    } catch (e) {
        throw (e);
    }

    let questionsArr: Array<QuestionData> = [];
    try {
        await Promise.all( [correct_response, incorrect_response] ).then(function () {
            correctOptionsArr.forEach(correct => {
                let options: Array<string | undefined> = [];
                options.push(correct);

                while (options.length < numOptions) {
                    options.push(incorrectOptionsArr.pop());
                }

                //sort options alphabetically
                options.sort(function (a,b) {
                    if (a !== undefined && b !== undefined) {
                        for (var i = 0; i < ARTICLES.length; i++) {
                            a = a.replace(ARTICLES[i] + " ", "");
                            b = b.replace(ARTICLES[i] + " ", "");
                        }
                
                        return (a > b) ? 1 : -1;
                    }
                
                    return 0;
                });

                //Set correct number of options based on difficulty selection and available languages for the answer if type is include
                if (type === 'include') {
                    let wordLangs = possibleLangs.shift();
                    if (questionDifficulty === 'hard') {
                        if (wordLangs) {
                            subjectLangs = wordLangs;
                            subjectLangs.sort();
                        }
                    } else if (questionDifficulty === 'moderate' && wordLangs && wordLangs[0] && wordLangs[1]) {
                        subjectLangs = wordLangs.slice(0,2);
                        subjectLangs.sort();
                        
                    } else {
                        if (wordLangs && wordLangs[0]) {
                            subjectLangs = [wordLangs[0]];
                            subjectLangs.sort();

                        }
                    }
                }
                if ( questionDifficulty === 'easy' ) {
                    //add clarifying text around certain languages (Update this when new langauges are added)
                    let langsFormatOne: Array<string>;
                    langsFormatOne = ['abenaki','afrikaans','algonquian','aramaic','balti','bengali','breton','catalan','cree','czech','gujarati','hindi','ilocano','inuit','javanese','lenape','malay','marquesan','nahuatl','occitan','ojibwa','quechua','tagalog','taino','tamil','tongan','urdu'];
                    
                    let langsFormatTwo: Array<string>;
                    langsFormatTwo = ['australian','croatian','hawaiian','irish','maori','persian','scots','serbian','tahitian',];
            
                    subjectLangs.forEach(function(lang, index) {
                        if (langsFormatOne.includes(lang)) {
                            subjectLangs[index] = "the language " + lang;
                        } else if  (langsFormatTwo.includes(lang)) {
                            subjectLangs[index] = "the " + lang + " language";
                        } else if (lang === 'english') {
                            subjectLangs[index] = 'modern English'
                        }
                    });
                }

                let question: QuestionData = {
                    type: 'origin-' + type,
                    options: options,
                    subject: correct,
                    subjectDiff: subjectDiffArr.shift()!,
                    subjectLangs: subjectLangs,
                    answer: correct,
                    reviewImgURL: imageURLArr.shift(),
                    reviewText: reviewTextArr.shift(),
                    audioURL: audioArr.shift()
                };

                question.options = question.options.filter((option) => {
                    return option !== undefined;
                });

                // If the request couldn't find words that match the requirements, quietly fail - just don't push the question
                if (question.options.length === numOptions) {
                    questionsArr.push(question);
                    if (debugWord) {
                        console.log("Built Question for ", debugWord, " Question type: ", "orgin");
                    }
                } else {
                    if (debugWord) {
                        console.log("Failed to build Question for ", debugWord);
                    }
                }
            });
        });
    } catch (e) {
        throw(e);
    }

    return questionsArr;
}

/**
 *  Returns multi origin questions e.g. "Which languages did WORD come from?" -> "French, Middle English"
 * 
 *  @param  {string}    questionDifficulty  Determines difficulty of option format
 *  @param  {string}    wordDifficulty      Optional API query for word difficulty filter
 *  @param  {string}    category            Optional API query for word category filter
 *  @param  {string}    debugWord           Word to be guaranteed as subject
 *  @return {promise}   promise             Resolves with question object or array of questions, Rejects with any error       
*/
//no check needed
async function buildOriginMultiQuestions(uuid: string, questionDifficulty: string, random_cat: boolean, wordDifficulty?: string, category?: string, numOptions: number = 1): Promise<Array<QuestionData>> {
    // Mod year by number according to difficulty, default easy (range of 50 years)
    let numAnswers: number = 1;
    if (questionDifficulty === 'moderate') numAnswers = 2;
    if (questionDifficulty === 'hard') numAnswers = 3;

   const ORIGIN_MULTI_EXCLUDE_AUTHORS_CHANCE = 0.75;
    const ORIGIN_MULTI_EXCLUDE_TYPE_CHANCE = 0.75;
    
    // Body of API call
    let body: WordListRequestBody = {
        debug:  DEBUG,
        uuid:   uuid,
        number: 1,
        min_origin_count: numAnswers,
        origin_combos: true,
        random_cat: random_cat,
    }

    if (Math.random() < ORIGIN_MULTI_EXCLUDE_AUTHORS_CHANCE) body.has_author= false;
    if (Math.random() < ORIGIN_MULTI_EXCLUDE_TYPE_CHANCE) body.usesubcat= false;

    // Apply word difficulty filter if present
    if (wordDifficulty !== undefined) body.difficulty = "easy";

    // Apply word category filter if present
    if (category !== undefined) body.category = category;

    // Apply debug word filter if present
    //if (debugWord !== undefined) body.debug_word = debugWord;

    // Helper function to process formatting for language names
    function formatLangNames (langs: Array<string>): Array<string> {
        langs.forEach((lang, i) => {
            // Remove hyphens
            lang = lang.replace('-', ' ');
      
            // Capitalize all first letters
            let words = lang.split(' ');
            words.forEach((word, j) => {
                words[j] = word[0].toUpperCase() + word.slice(1);
            });

            langs[i] = words.join(' ');
        });
      
        return langs;
    }

    let words: Array<WordJSON> = [];
    let origin_combos_moderate: Array<Array<string>> = [];
    let origin_combos_hard: Array<Array<string>> = [];
    let origin_easy_langs: Array<string> = [];
    try {
        let response = await axios.post(apiURI +'/wordsupdated/list2', body, {});
        words = response.data.data;
        origin_easy_langs = response.data.origin_easy_langs;
    } catch (e) {
        throw(e);
    }

    let questionsArr: Array<QuestionData> = [];
    for (let wordID in words) {
        let originLangs: Array<string> = words[wordID].language_of_origin,
            reviewText:  string        = words[wordID].sentence,
            imageURL:    string        = wordigins_chooseRand(words[wordID].images)[0];
        
        // Alternate logic to choose randomly
        // let answerLangs: Array<string> = (originLangs.length > numAnswers) ? wordigins_chooseRand(originLangs, numAnswers) : originLangs;

        // Choose enough languages for answer by least-frequent languages of origin
        let originLangsByFrequency: Array<string> = ORIGIN_LANGUAGES.filter(el => originLangs.indexOf(el) >= 0);
        let answerLangs: Array<string> = [];
        for (let i = 0; i < numAnswers; i++) {
            let lang = originLangsByFrequency.pop();
            if (lang) {
                answerLangs.push(lang);
            }
        }

        //reverse answer langs array so it is alphabetical in game
        answerLangs = answerLangs.reverse();

        // Declare options with correct option
        let options: Array<string> = [];

        // Get all possible origin languages not already associated with the correct answer
        let associatedLangs = originLangs.concat(words[wordID].origin_exclude || []);
        let incorrectLangs: Array<string> = [];
        if ( origin_easy_langs ) {
            const formattedAssociated = formatLangNames(associatedLangs);
            incorrectLangs = origin_easy_langs.filter(el => formattedAssociated.indexOf(el) < 0);
        } else {
            incorrectLangs = ORIGIN_LANGUAGES.filter(el => associatedLangs.indexOf(el) < 0);
            incorrectLangs = formatLangNames(incorrectLangs);
        }
        let attempts = 0;
        const maxAttempts = 100;
        while (options.length < numOptions - 1 && attempts < maxAttempts) {
            const pick = wordigins_chooseRand(incorrectLangs, 1)[0];
            if (pick && !options.includes(pick)) {
                options.push(pick);
            }
            attempts++;
        }

        //add correct answer to options
        const correctAnswer = originLangs?.[0] ? (originLangs[0].charAt(0).toUpperCase() + originLangs[0].slice(1)) : undefined;
        if (!correctAnswer) {;
            console.log('No correct answer found for word ' + words[wordID].phrase);
            continue;
        }
        options.push(correctAnswer);

        //sort options alphabetically
        options.sort(function (a,b) {
            if (a !== undefined && b !== undefined) {
                for (var i = 0; i < ARTICLES.length; i++) {
                    a = a.replace(ARTICLES[i] + " ", "");
                    b = b.replace(ARTICLES[i] + " ", "");
                }
        
                return (a > b) ? 1 : -1;
            }
        
            return 0;
        });

        let question: QuestionData = {
            type: 'origin-multi',
            reviewText: reviewText,
            reviewImgURL: imageURL,
            category: category,
            subject: words[wordID].phrase,
            subjectDiff: words[wordID].difficulty,
            options: options,
            answer: correctAnswer,
            audioURL: words[wordID].audio
        };
        question.options = question.options.filter((option) => {
            return option !== undefined;
        });

        if (question.options.length === numOptions) questionsArr.push(question);
    }

    return questionsArr;
}

/**
 *  Returns subcategory questions e.g. "Which word came from/did not come from a play"
 * 
 *  @param  {string}    questionDifficulty  Determines difficulty of option format
 *  @param  {string}    includeOrExclude    Value 'include' or 'exclude' can be passed to force a type, default false causes random choice
 *  @param  {string}    wordDifficulty      Optional API query for word difficulty filter
 *  @param  {string}    category            Optional API query for word category filter
 *  @param  {string}    debugWord           Word to be guaranteed as correct answer
 *  @return {promise}   promise             Resolves with question object or array of questions, Rejects with any error       
*/
//check on incorrect body for both, need to send word
async function buildSubcatQuestions(uuid: string, questionDifficulty: string, random_cat: boolean, includeOrExclude?: string, wordDifficulty?: string, category?: string, debugWord?: string, numOptions: number = 1): Promise<Array<QuestionData>> {
    // Set type, random if not passed
    let randType = (Math.floor(Math.random() * 2)) ? 'include' : 'exclude';
    let type = (includeOrExclude) ? includeOrExclude : randType;
    let correctBody: WordListRequestBody, incorrectBody: WordListRequestBody;

    const SUBCAT_CORRECT_EXCLUDE_AUTHORS_CHANCE = 0.75;

    correctBody = {
        debug:  DEBUG,
        uuid:   uuid,
        number: 1,
        category: category,
        usesubcat: true,
        random_cat: random_cat,
    }

    if (Math.random() < SUBCAT_CORRECT_EXCLUDE_AUTHORS_CHANCE) correctBody.has_author= false;

    // Apply word difficulty and category filters if present
    if (wordDifficulty !== undefined) correctBody.difficulty = wordDifficulty;

    if (category !== undefined) correctBody.category = category;

    // Apply debug word filter if present
    if (debugWord !== undefined) {
       correctBody.debug_word = debugWord;
    }

    let correctOptionsArr: Array<string> = [],
        imageURLArr: Array<string> = [],
        reviewTextArr: Array<string> = [],
        audioArr: Array<string> = [],
        correct_subcats_exclude: Array<string> = [],
        articleArr: Array<string> = [],
        subcatArr: Array<string> =[],
        subjectDiffArr: Array<string> = [];

    try {
        var correct_response = await axios.post(apiURI + '/wordsupdated/list2', correctBody, {});

        let correct_wordData: Array<WordJSON> = correct_response.data.data;
        var correct = "";
        for (let wordID in correct_wordData) {
            correctOptionsArr.push(correct_wordData[wordID].phrase);
            imageURLArr.push(wordigins_chooseRand(correct_wordData[wordID].images)[0]);
            reviewTextArr.push(correct_wordData[wordID].sentence);
            audioArr.push(correct_wordData[wordID].audio);
            articleArr.push(correct_wordData[wordID].article);
            subcatArr.push(correct_wordData[wordID].subcat);
            subjectDiffArr.push(correct_wordData[wordID].difficulty);
            correct_subcats_exclude = [...new Set([...correct_wordData[wordID].subcat_exclude,...correct_subcats_exclude])];
            correct = correct_wordData[wordID].phrase;

        }

    } catch (e) {
        throw (e);
    } 

    let subcat_flat = subcatArr.flat();
    let correct_subcats_exclude_flat = correct_subcats_exclude.flat();
    if (subcat_flat.includes("music")) {
        subcat_flat.push("musical-instrument");
    } else if (subcat_flat.includes("musical-instrument")) {
        subcat_flat.push("music");
    }
        if (type === 'include') {

            const SUBCAT_INCLUDE_INCORRECT_EXCLUDE_AUTHORS_CHANCE = 0.35;

            incorrectBody = {
                debug:  false,
                uuid:   uuid,
                category: category,
                correct_subcats: JSON.stringify(subcat_flat),
                exclude_subcats: JSON.stringify(subcat_flat),
                number: numOptions - 1,
                usesubcat: true,
                random_cat: random_cat,
                //check_similar: true, remove wordigins check similar functionality
                correct: correct,
                english_only: true
            }

            if (Math.random() < SUBCAT_INCLUDE_INCORRECT_EXCLUDE_AUTHORS_CHANCE) incorrectBody.has_author = false;

        // Apply word difficulty filter if present
            if (wordDifficulty !== undefined) incorrectBody.difficulty = wordDifficulty;

            // Apply word category filter if present
            if (category !== undefined) incorrectBody.category = category;

            let incorrectOptionsArr: Array<string> = [];
            try {
                var incorrect_response = await axios.post( apiURI +'/wordsupdated/list2', incorrectBody, {});

                let incorrect_wordData: Array<WordJSON> = incorrect_response.data.data;
                for (let wordID in incorrect_wordData) {
                    incorrectOptionsArr.push(incorrect_wordData[wordID].phrase);
                }
            } catch (e) {
                throw (e);
            }

            let questionsArr: Array<QuestionData> = [];
            try {
                await Promise.all( [correct_response, incorrect_response] ).then(function () {
                    correctOptionsArr.forEach(correct => {
                        let current_subcat = subcat_flat.shift();
                        if (current_subcat === "musical-instrument") {
                            current_subcat = "musical instrument";
                        }
                        let options: Array<string | undefined> = [];
                        if (correct === "musical-instrument") {
                            correct = "musical instrument";
                        }
                        options.push(correct);

                        while (options.length < numOptions) {
                            let incorrect = incorrectOptionsArr.pop();
                            if (incorrect === "musical-instrument") {
                                incorrect = "musical instrument";
                            }
                            options.push(incorrect);
                        }

                        //sort options alphabetically
                        options.sort(function (a,b) {
                            if (a !== undefined && b !== undefined) {
                                for (var i = 0; i < ARTICLES.length; i++) {
                                    a = a.replace(ARTICLES[i] + " ", "");
                                    b = b.replace(ARTICLES[i] + " ", "");
                                }
                        
                                return (a > b) ? 1 : -1;
                            }
                        
                            return 0;
                        });

                        let question: QuestionData = {
                            type: 'subcat-' + type,
                            options: options,
                            subject: correct,
                            answer: correct,
                            subcat: current_subcat,
                            reviewImgURL: imageURLArr.shift(),
                            reviewText: reviewTextArr.shift(),
                            subjectDiff: subjectDiffArr.shift()!,
                            audioURL: audioArr.shift(),
                            article: articleArr.shift()
                        };

                        question.options = question.options.filter((option) => {
                            return option !== undefined;
                        });

                        // If the request couldn't find words that match the requirements, quietly fail - just don't push the question
                        if (question.options.length === numOptions) {
                            questionsArr.push(question);
                            if (debugWord) {
                                console.log("Built Question for ", debugWord, " Question type: ", "subcat-exclude");
                            }
                        } else {
                            if (debugWord) {
                                console.log("Failed to build Question for ", debugWord);
                            }
                        }
                    });
                });
            } catch (e) {
                throw(e);
            }
            return questionsArr;

        } else if (type === 'exclude') {
           
            let subcats_to_exclude = [...new Set([...subcat_flat,...correct_subcats_exclude_flat])];
            let filtered_subcats = SUBCATEGORIES.filter(item => subcats_to_exclude.indexOf(item) < 0);
            let subcat = wordigins_chooseRand(filtered_subcats)[0];
           
            incorrectBody = {

                debug:  false,
                uuid:   uuid,
                category: category,
                number: numOptions - 1,
                subcat: subcat,
                random_cat: random_cat,
                //check_similar: true, remove wordigins check similar functionality
                correct: correct,
                english_only: true
            }

        // Apply word difficulty filter if present
            if (wordDifficulty !== undefined) incorrectBody.difficulty = wordDifficulty;

            // Apply word category filter if present
            if (category !== undefined) incorrectBody.category = category;

            let incorrectOptionsArr: Array<string> = [];
            let exclude_articleArr: Array<string> = []
            try {
                var incorrect_response = await axios.post( apiURI +'/wordsupdated/list2', incorrectBody, {});

                let incorrect_wordData: Array<WordJSON> = incorrect_response.data.data;
                for (let wordID in incorrect_wordData) {
                    incorrectOptionsArr.push(incorrect_wordData[wordID].phrase);
                    exclude_articleArr.push(incorrect_wordData[wordID].article);
                }
            } catch (e) {
                throw (e);
            }
            let questionsArr: Array<QuestionData> = [];
            try {
                await Promise.all( [correct_response, incorrect_response] ).then(function () {
                    correctOptionsArr.forEach(correct => {
                        if (subcat === "musical-instrument") {
                            subcat = "musical instrument";
                        }
                        let options: Array<string | undefined> = [];
                        if (correct === "musical-instrument") {
                            correct = "musical instrument";
                        }
                        options.push(correct);

                        while (options.length < numOptions) {
                            let incorrect = incorrectOptionsArr.pop();
                            if (incorrect === "musical-instrument") {
                                incorrect = "musical instrument";
                            }
                            options.push(incorrect);
                        }

                        //sort options alphabetically
                        options.sort(function (a,b) {
                            if (a !== undefined && b !== undefined) {
                                for (var i = 0; i < ARTICLES.length; i++) {
                                    a = a.replace(ARTICLES[i] + " ", "");
                                    b = b.replace(ARTICLES[i] + " ", "");
                                }
                        
                                return (a > b) ? 1 : -1;
                            }
                        
                            return 0;
                        });

                        let question: QuestionData = {
                            type: 'subcat-' + type,
                            options: options,
                            subject: correct,
                            answer: correct,
                            subcat: subcat,
                            reviewImgURL: imageURLArr.shift(),
                            reviewText: reviewTextArr.shift(),
                            subjectDiff: subjectDiffArr.shift()!,
                            audioURL: audioArr.shift(),
                            article: exclude_articleArr.shift()
                        };

                        question.options = question.options.filter((option) => {
                            return option !== undefined;
                        });

                        // If the request couldn't find words that match the requirements, quietly fail - just don't push the question
                        if (question.options.length === numOptions) {
                            questionsArr.push(question);
                            if (debugWord) {
                                console.log("Built Question for ", debugWord, " Question type: ", "subcat-exclude");
                            }
                        } else {
                            if (debugWord) {
                                console.log("Failed to build Question for ", debugWord);
                            }
                        }
                    });
                });
            } catch (e) {
                throw(e);
            }
            return questionsArr;


     } else {
        return Promise.reject(new Error('Invalid question type: ' + type));
    }
}

/**
 *  Returns subcat origin questions e.g. "The phrase Jumbo comes from:" -> "an animal"
 * 
 *  @param  {string}    questionDifficulty  Determines difficulty of option format
 *  @param  {string}    wordDifficulty      Optional API query for word difficulty filter
 *  @param  {string}    category            Optional API query for word category filter
 *  @param  {string}    debugWord           Word to be guaranteed as subject
 *  @return {promise}   promise             Resolves with question object or array of questions, Rejects with any error       
*/
//no check needed
async function buildSubcatOriginQuestions(uuid: string, questionDifficulty: string, random_cat: boolean, wordDifficulty?: string, category?: string, debugWord?: string, numOptions: number = 1): Promise<Array<QuestionData>> {
    // Body of API call

    const SUBCAT_ORIGIN_CORRECT_EXCLUDE_AUTHORS_CHANCE = 0.75;

    let correctBody: WordListRequestBody = {
        debug:  DEBUG,
        uuid:   uuid,
        number: 1,
        usesubcat: true,
        random_cat: random_cat,
    }

    if (Math.random() < SUBCAT_ORIGIN_CORRECT_EXCLUDE_AUTHORS_CHANCE) correctBody.has_author = false;


    // Apply word difficulty filter if present
    if (wordDifficulty !== undefined) correctBody.difficulty = wordDifficulty;

    // Apply word category filter if present
    if (category !== undefined) correctBody.category = category;

    // Apply debug word filter if present
    if (debugWord !== undefined) correctBody.debug_word = debugWord;

    let words: Array<WordJSON> = [],
        questionsArr: Array<QuestionData> = [],
        correct_subcats_exclude: Array<string> = [],
        correctOptionsArr: Array<string> = [],
        subcatArr: Array<string> =[],
        imageURLArr: Array<string> = [],
        reviewTextArr: Array<string> = [],
        audioArr: Array<string> = [],
        subjectDiffArr: Array<string> = [],
        articleArr: Array<string> = []
    try {
        var correctResponse = await axios.post(apiURI +'/wordsupdated/list2', correctBody, {});
        words = correctResponse.data.data;

        for (let wordID in words) {
            subcatArr.push(words[wordID].subcat);
            imageURLArr.push(wordigins_chooseRand(words[wordID].images)[0]);
            correctOptionsArr.push(words[wordID].phrase);
            articleArr.push(words[wordID].article);
            subjectDiffArr.push(words[wordID].difficulty);
            reviewTextArr.push(words[wordID].sentence);
            audioArr.push(words[wordID].audio);
            correct_subcats_exclude = [...new Set([...words[wordID].subcat_exclude,...correct_subcats_exclude])]
        }

    } catch (e) {
        throw(e);
    }
    
    let subcat_flat = subcatArr.flat();
    let correct_subcats_exclude_flat = correct_subcats_exclude.flat();
    let subcats_to_exclude = [...new Set([...subcat_flat,...correct_subcats_exclude_flat])];

    const SUBCAT_ORIGIN_INCORRECT_EXCLUDE_AUTHORS_CHANCE = 0.75;

    let incorrectBody: WordListRequestBody = {
        debug:  DEBUG,
        uuid:   uuid,
        number: 3,
        correct_subcats: JSON.stringify(subcats_to_exclude),
        usesubcat: true,
        random_cat: random_cat,
        english_only: true
    }

    if (Math.random() < SUBCAT_ORIGIN_INCORRECT_EXCLUDE_AUTHORS_CHANCE) incorrectBody.has_author = false;

        // QUESTION: will this limit answers to only the difficulty and if so, is there some kind
        //           of difficulty range in the API, rather than just either a single difficulty, or
        //            all difficulties?
        //          Seems like there  is in WordupdatedController, but not sure if it works
        //          random cat = always one difficulty
        
        // Apply word difficulty filter if present
        if (wordDifficulty !== undefined) incorrectBody.difficulty = wordDifficulty;


            let incorrectOptionsArr: Array<string> = [];
            let incorrectArticleArr: Array<string> = [];
            let incorrectSubcatArr: Array<string> =[];
            try {
                var incorrectResponse = await axios.post( apiURI +'/wordsupdated/list2', incorrectBody, {});

                let incorrect_wordData: Array<WordJSON> = incorrectResponse.data.data;
                for (let wordID in incorrect_wordData) {
                    incorrectOptionsArr.push(incorrect_wordData[wordID].phrase);
                    incorrectArticleArr.push(incorrect_wordData[wordID].article);
                    incorrectSubcatArr.push(incorrect_wordData[wordID].subcat);
                }
            } catch (e) {
                throw (e);
            }

            //create arrays of unique articles and subcats
            let uniqueSubcatArr: string[] = [];
            let uniqueArticleArr: string[] = [];
            for (let i = 0; i < incorrectSubcatArr.length; i++) {
                if (!uniqueSubcatArr.includes(incorrectSubcatArr[i])) {
                    uniqueSubcatArr.push(incorrectSubcatArr[i]);
                    uniqueArticleArr.push(incorrectArticleArr[i]);
                }
              }

        try {
            await Promise.all( [correctResponse, incorrectResponse] ).then(function () {
                subcat_flat.forEach(correct => {
                    let options: Array<string | undefined> = [];
                    let correctArticle = articleArr.shift();
                    if (correct === "bible") {
                        correct = "Bible";
                    } else if (correct === "magazine-newspaper") {
                        correct = "magazine/newspaper";
                    } else if (correct === "movies-tv") {
                        correct = "Movies/TV";
                    } else if (correct === "music") {
                        correct = "music vernacular";
                    } else if (correct === "place") {
                        correct = "place name";
                    }
                    let correctFull = correct;
                    if (correctArticle) {
                        correctFull = correctArticle.slice(1, -1) + correct;
                    }
                    options.push(correctFull);
                    while (options.length < numOptions) {
                        let incorrectArticle = uniqueArticleArr.pop();
                        let incorrectSubcat = uniqueSubcatArr.pop();

                        if (incorrectSubcat === "bible") {
                            incorrectSubcat = "Bible";
                        } else if (incorrectSubcat === "magazine-newspaper") {
                            incorrectSubcat = "magazine/newspaper";
                        } else if (incorrectSubcat === "movies-tv") {
                            incorrectSubcat = "Movies/TV";
                        } else if (incorrectSubcat === "music") {
                            incorrectSubcat = "music vernacular";
                        } else if (incorrectSubcat === "place") {
                            incorrectSubcat = "place name";
                        }

                        if (incorrectArticle && incorrectSubcat) {
                            options.push(incorrectArticle.slice(1, -1) + incorrectSubcat);
                        } else if (incorrectSubcat){
                            options.push(incorrectSubcat);
                        } else {
                            break;
                        }
                    }

                    //sort options alphabetically
                    options.sort(function (a,b) {
                        if (a !== undefined && b !== undefined) {
                            for (var i = 0; i < ARTICLES.length; i++) {
                                a = a.replace(ARTICLES[i] + " ", "");
                                b = b.replace(ARTICLES[i] + " ", "");
                            }
                    
                            return (a > b) ? 1 : -1;
                        }
                    
                        return 0;
                    });

                    let subject = correctOptionsArr.shift();
                    if (subject ){
                        let question: QuestionData = {
                            type: 'subcat-multi',
                            options: options,
                            subject: subject,
                            answer: correctFull,
                            reviewImgURL: imageURLArr.shift(),
                            reviewText: reviewTextArr.shift(),
                            subjectDiff: subjectDiffArr.shift()!,
                            audioURL: audioArr.shift(),
                        };

                        question.options = question.options.filter((option) => {
                            return option !== undefined;
                        });

                        // If the request couldn't find words that match the requirements, quietly fail - just don't push the question
                        if (question.options.length === numOptions) {
                            questionsArr.push(question); 
                        }
                    }
                });
            });
        } catch (e) {
            throw(e);
        }

    return questionsArr;
}
/**
 *  Author include/exclude:
 *  - 'author-include': Which of these phrases is coined by AUTHOR?
 *  - 'author-exclude': Which of these phrases is NOT coined by AUTHOR?
 */
async function buildAuthorQuestions( uuid: string, questionDifficulty: string, random_cat: boolean, includeOrExclude?: string, wordDifficulty?: string, category?: string, debugWord?: string, numOptions: number = 1 ): Promise<Array<QuestionData>> {
    const LOG = `[AuthorQ-${Math.floor(Math.random() * 10000)}]`;


    let type = includeOrExclude === 'include' || includeOrExclude === 'exclude' ? includeOrExclude : 'include';
    
    const AUTHOR_CORRECT_EXCLUDE_TYPES_CHANCE = 0.75;

    // get an author from a phrase
    let correctBody: WordListRequestBody = {
        debug:  DEBUG,
        uuid:   uuid,
        number: 1,
        random_cat: random_cat,
        has_author: true
    };

    if (Math.random() < AUTHOR_CORRECT_EXCLUDE_TYPES_CHANCE && questionDifficulty !== 'moderate') correctBody.usesubcat = false;

    const AUTHOR_INCORRECT_EXCLUDE_TYPES_CHANCE = 0.75;

    // Pool pull for options
    let poolBody: WordListRequestBody = {
        debug:  false,
        uuid:   uuid,
        number: numOptions + 9,
        random_cat: random_cat,
        english_only: true
        //check_similar: true remove wordigins check similar functionality
    };

    if (Math.random() < AUTHOR_INCORRECT_EXCLUDE_TYPES_CHANCE && questionDifficulty !== 'moderate') poolBody.usesubcat = false;

    if (wordDifficulty !== undefined) {
        correctBody.difficulty = wordDifficulty;
        poolBody.difficulty = wordDifficulty;
    }
    if (category !== undefined) {
        correctBody.category = category;
        poolBody.category = category;
    }
    if (debugWord !== undefined) {
        correctBody.debug_word = debugWord;
    }


    let questionsArr: Array<QuestionData> = [];

    try {
        const correct_response = await axios.post( apiURI + '/wordsupdated/list2', correctBody, ENVIRONMENT === 'production' ? { auth: { username: "igins", password: "llc" } } : {}
        );
        const pool_response = await axios.post( apiURI + '/wordsupdated/list2', poolBody, ENVIRONMENT === 'production' ? { auth: { username: "igins", password: "llc" } } : {}
        );

        if (!correct_response || !pool_response) {
            console.error(`${LOG} missing axios response(s)`, { correct_response, pool_response });
            return questionsArr;
        }

        const correct_wordData: any = correct_response.data?.data;
        const pool_wordData: any = pool_response.data?.data;

        const correctKeys = correct_wordData ? Object.keys(correct_wordData) : [];
        const poolKeys = pool_wordData ? Object.keys(pool_wordData) : [];

        if (correctKeys.length === 0) {
            console.warn(`${LOG} no correct_wordData returned`);
            return questionsArr;
        }
        if (poolKeys.length === 0) {
            console.warn(`${LOG} no pool_wordData returned`);
            return questionsArr;
        }

        // subject is the author
        let subjectAuthor: string | null = null;
        let subjectDiff: string = '';
        let reviewText: string = '';
        let reviewImgURL: string = '';
        let audioURL: string = '';
        let seedPhrase: string | null = null;
        let optionsDates: { [k: string]: string } = {};

        for (let wordID in correct_wordData) {
            const phrase = correct_wordData[wordID];
            if (!phrase) continue;
            if (phrase && phrase.author && String(phrase.author).trim() !== '') {
                subjectAuthor = String(phrase.author);
                seedPhrase = String(phrase.phrase);
                subjectDiff = String(phrase.difficulty || '');
                reviewText  = String(phrase.sentence || '');
                audioURL    = String(phrase.audio || '');
                if (phrase.images && phrase.images.length) {
                    reviewImgURL = phrase.images[0];
                }
                if (phrase.researched_date && seedPhrase) {
                    optionsDates[seedPhrase] = String(phrase.researched_date);
                }
                break;
            }
        }
        if (!subjectAuthor) {
            return questionsArr;
        }

        let options: Array<string | undefined> = [];
        let answer: string | undefined;
        let byAuthor: string[] = [];
        let notByAuthor: string[] = [];

        //step through all other options and bin phrases into byAuthor and notByAuthor for choosing incorrect
        for (let wordID in pool_wordData) {
            const phrase = pool_wordData[wordID];
            if (!phrase || !phrase.phrase || !phrase.author) continue;
            const author = String(phrase.author);
            const phr = String(phrase.phrase);
            if (phr === seedPhrase) continue;

            if (author === subjectAuthor) {
                if (!byAuthor.includes(phr)) {
                    byAuthor.push(phr);
                    if (phrase.researched_date) optionsDates[phr] = String(phrase.researched_date);
                }
            } else {
                if (!notByAuthor.includes(phr)) {
                    notByAuthor.push(phr);
                    if (phrase.researched_date) optionsDates[phr] = String(phrase.researched_date);
                }
            }
            if (byAuthor.length > numOptions * 4 && notByAuthor.length > numOptions * 4) break;
        }

        if (type === 'include') {
            // Correct is a phrase BY the author; prefer the seed phrase
            if (seedPhrase) {
                answer = seedPhrase;
                options.push(seedPhrase);
            } else if (byAuthor.length > 0) {
                answer = byAuthor.pop();
                options.push(answer);
            }

            // Fill the rest with phrases NOT by the author
            while (options.length < numOptions && notByAuthor.length) {
                const inc = notByAuthor.pop();
                if (inc && !options.includes(inc)) options.push(inc);
            }
        } else {
            // exclude: correct is NOT by the author
            if (notByAuthor.length > 0) {
                answer = notByAuthor.pop();
                if (answer) options.push(answer);
            }

            // Fill the rest with phrases by the author
            while (options.length < numOptions && byAuthor.length) {
                const inc = byAuthor.pop();
                if (inc && !options.includes(inc)) options.push(inc);
            }
        }

        if (options.length < numOptions || !answer) {
            console.warn(`${LOG} returning empty, options/answer invalid`, {
                options_len: options.length, numOptions, hasAnswer: !!answer, type
            });
            return questionsArr;
        }

        wordigins_shuffleArr(options);

        const question: QuestionData = {
            type: 'author-' + type,
            reviewText: reviewText,
            reviewImgURL: reviewImgURL,
            category: category,
            subject: subjectAuthor!,
            subjectDiff: subjectDiff,
            options: options,
            optionsDates: optionsDates,
            answer: answer!,
            audioURL: audioURL,
            author: subjectAuthor,
        };

        // prune undefineds
        const beforePrune = question.options.length;
        question.options = question.options.filter((o) => o !== undefined);
        if (beforePrune !== question.options.length) {
        }

        if (question.options.length === numOptions) {
            questionsArr.push(question);
        }
    } catch (e: any) {
        const status = e?.response?.status;
        const data = e?.response?.data;
        console.error(`${LOG} axios error`, { status, data, message: e?.message });
    }
    return questionsArr;
}

/**
 *  Author origin:
 *  "Who coined the phrase PHRASE?"
 */
async function buildAuthorOriginQuestions(
    uuid: string,
    questionDifficulty: string,
    random_cat: boolean,
    wordDifficulty?: string,
    category?: string,
    debugWord?: string,
    numOptions: number = 1
): Promise<Array<QuestionData>> {

    const LOG = '[AuthorOrigin]';

    const AUTHOR_ORIGIN_CORRECT_EXCLUDE_TYPES_CHANCE = 0.75;

    let correctBody: WordListRequestBody = {
        debug:  false,
        uuid:   uuid,
        number: 1,
        random_cat: random_cat,
        has_author: true
    };

    if (Math.random() < AUTHOR_ORIGIN_CORRECT_EXCLUDE_TYPES_CHANCE && questionDifficulty !== 'moderate') correctBody.usesubcat = false;

    const AUTHOR_ORIGIN_INCORRECT_EXCLUDE_TYPES_CHANCE = 0.75;

    let poolBody: WordListRequestBody = {
        debug:  false,
        uuid:   uuid,
        number: numOptions + 13,
        random_cat: random_cat,
        has_author: true,
        english_only: true
        //check_similar: true remove wordigins check similar functionality
    };

    if (Math.random() < AUTHOR_ORIGIN_INCORRECT_EXCLUDE_TYPES_CHANCE && questionDifficulty !== 'moderate') poolBody.usesubcat = false;

    if (wordDifficulty !== undefined) {
        correctBody.difficulty = wordDifficulty;
        poolBody.difficulty = wordDifficulty;
    }
    if (category !== undefined) {
        correctBody.category = category;
        poolBody.category = category;
    }
    if (debugWord !== undefined) {
        correctBody.debug_word = debugWord;
    }

    let questionsArr: Array<QuestionData> = [];

    try {
        const correct_response = await axios.post(
            apiURI + '/wordsupdated/list2',correctBody, ENVIRONMENT === 'production' ? { auth: { username: "igins", password: "llc" } } : {}
        );
        const pool_response = await axios.post( apiURI + '/wordsupdated/list2', poolBody, ENVIRONMENT === 'production' ? { auth: { username: "igins", password: "llc" } } : {}
        );

        if (!correct_response || !pool_response) {
            console.error(`${LOG} missing axios response(s)`, { correct_response, pool_response });
            return questionsArr;
        }

        const correct_wordData: any = correct_response.data?.data;
        const pool_wordData: any = pool_response.data?.data;

        const correctKeys = correct_wordData ? Object.keys(correct_wordData) : [];
        const poolKeys = pool_wordData ? Object.keys(pool_wordData) : [];

        if (correctKeys.length === 0) {
            console.warn(`${LOG} no correct_wordData returned`);
            return questionsArr;
        }
        if (poolKeys.length === 0) {
            console.warn(`${LOG} no pool_wordData returned`);
            return questionsArr;
        }

        let subjectPhrase: string | null = null;
        let correctAuthor: string | null = null;
        let reviewText: string = '';
        let reviewImgURL: string = '';
        let audioURL: string = '';
        let subjectDiff: string = '';

        for (let wordID in correct_wordData) {
            const phrase = correct_wordData[wordID];
            if (!phrase) continue;
            if (phrase && phrase.author && String(phrase.author).trim() !== '') {
                subjectPhrase = String(phrase.phrase);
                correctAuthor = String(phrase.author);
                subjectDiff   = String(phrase.difficulty || '');
                reviewText    = String(phrase.sentence || '');
                audioURL      = String(phrase.audio || '');
                if (phrase.images && phrase.images.length) {
                    reviewImgURL = phrase.images[0];
                }
                break;
            }
        }

        if (!subjectPhrase || !correctAuthor) {
            console.warn(`${LOG} missing subjectPhrase or correctAuthor`, { subjectPhrase, correctAuthor, sample: correct_wordData[correctKeys[0]] });
            return questionsArr;
        }

        // Build distinct author options
        let options: Array<string | undefined> = [correctAuthor];
        let seen = new Set<string>([correctAuthor]);

        for (let wordID in pool_wordData) {
            const phrase = pool_wordData[wordID];
            if (!phrase || !phrase.author) continue;
            const name = String(phrase.author);
            if (!name || seen.has(name)) continue;
            seen.add(name);
            options.push(name);
            if (options.length >= numOptions) break;
        }

        if (options.length < numOptions) {
            console.warn(`${LOG} returning empty, insufficient distinct authors`, {
                have: options.length, need: numOptions
            });
            return questionsArr;
        }

        const optionsPreShuffle = options.slice();
        wordigins_shuffleArr(options);
        const question: QuestionData = {
            type: 'author-origin',
            reviewText: reviewText,
            reviewImgURL: reviewImgURL,
            category: category,
            subject: subjectPhrase!,
            subjectDiff: subjectDiff,
            options: options,
            answer: correctAuthor,
            audioURL: audioURL,
        };

        // prune undefineds
        const beforePrune = question.options.length;
        question.options = question.options.filter((o) => o !== undefined);
        if (beforePrune !== question.options.length) {
        }

        if (question.options.length === numOptions) {
            questionsArr.push(question);
            if (debugWord) {
                console.log(`${LOG} debug built for`, debugWord, 'Question type:', question.type);
            }
        } else {
            console.warn(`${LOG} built question discarded due to option count mismatch`, {
                have: question.options.length, need: numOptions
            });
        }
    } catch (e: any) {
        const status = e?.response?.status;
        const data = e?.response?.data;
        console.error(`${LOG} axios error`, { status, data, message: e?.message });
        // fall through with empty questionsArr
    }
    return questionsArr;
}
